import { Tooltip as ChakraTooltip, TooltipProps } from "@chakra-ui/react";
import { ReactNode, useState } from "react";
import { cn } from "utils/helpers";

interface CustomTooltipProps extends TooltipProps {
	children: ReactNode;
	className?: string;
	label: ReactNode;
	placement?: TooltipProps["placement"];
	shouldWrapChildren?: boolean;
	/**
	 * Open on hover ONLY — never on keyboard focus.
	 *
	 * Chakra opens tooltips on focus as well as hover, and `shouldWrapChildren` injects
	 * a `<span tabIndex={0}>` to make that possible. That combination is a trap inside a
	 * modal: Chakra's focus lock focuses the first tabbable node on open, so if a
	 * tooltipped element is first, the tooltip fires by itself. The scoring modal's
	 * company logo did exactly that — its tooltip label is a large copy of the logo, so
	 * it appeared blown up on load as if hovered.
	 *
	 * Use ONLY where the tooltip adds no information a keyboard user would otherwise
	 * miss (e.g. a decorative enlargement of an image that is already on screen).
	 * For tooltips carrying real content, leave this off: dropping the focus trigger
	 * would hide that content from keyboard users.
	 */
	openOnHoverOnly?: boolean;
}

const Tooltip = ({
	label,
	placement,
	className,
	children,
	shouldWrapChildren = true,
	openOnHoverOnly = false,
	...props
}: CustomTooltipProps) => {
	const [hoverOpen, setHoverOpen] = useState(false);

	const sharedClassName = cn(
		"!border !border-gray-200 !bg-gray-900 !bg-opacity-80 !shadow-lg",
		className
	);

	if (openOnHoverOnly) {
		return (
			<ChakraTooltip
				label={label}
				placement={placement}
				hasArrow
				// Controlled: Chakra's own focus/hover triggers are bypassed entirely.
				isOpen={hoverOpen}
				// We supply the wrapper ourselves so it carries NO tabIndex — the element
				// stays out of the tab order and focus locks skip straight past it.
				shouldWrapChildren={false}
				className={sharedClassName}
				{...props}
			>
				<span
					className="inline-flex"
					onMouseEnter={() => setHoverOpen(true)}
					onMouseLeave={() => setHoverOpen(false)}
					data-testid="tooltip-hover-target"
				>
					{children}
				</span>
			</ChakraTooltip>
		);
	}

	return (
		<ChakraTooltip
			label={label}
			placement={placement}
			hasArrow
			shouldWrapChildren={shouldWrapChildren}
			className={sharedClassName}
			{...props}
		>
			{children}
		</ChakraTooltip>
	);
};

export default Tooltip;
